import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';

import { FormArrayExtended } from './formArrayExtended';
import { FormMap } from './formMap';
import { PDFDocument } from 'pdf-lib';
import { UserRole } from '@common/enums/UserRole';
import { APP_CONSTANTS } from './app-constants';
import { ActivatedRoute } from '@angular/router';
import { map, Observable, of } from 'rxjs';

export type ControlFactory = () => AbstractControl;

/**
 * Recursively sets values of nested controls so nested object === null/undefined
 * does not cause failure as in case of AbstractControl.patchValue
 * @param control target FormControl, FormArray or FormGroup
 * @param value source data
 */
export function safeSetValue(control: AbstractControl, value: any) {
    if (control instanceof FormControl) {
        control.setValue(value, { emitEvent: false });

        return;
    }

    if (control instanceof FormArrayExtended) {
        if (nullOrUndef(value)) {
            value = [];
        }

        if (!Array.isArray(value)) {
            throw new TypeError(`Cannot set value '${value}' on FormArrayExtended`);
        }

        control.setSize(value.length);
        control.controls.forEach((c, idx) => safeSetValue(c, value[idx]));
    } else if (control instanceof FormMap) {
        if (nullOrUndef(value)) {
            value = {};
        }

        if (typeof value !== 'object' || Array.isArray(value)) {
            throw new TypeError(`Cannot set value '${value}' on FormMap`);
        }

        control.setShape(Object.keys(value));
        Object.entries(control.controls).forEach(([name, c]) => safeSetValue(c, value[name]));
    } else if (control instanceof FormArray) {
        control.controls.forEach((child, idx) =>
            safeSetValue(child, getValue(value, idx)));
    } else if (control instanceof FormGroup) {
        Object.keys(control.controls).forEach((name) => {
            safeSetValue(control.controls[name], getValue(value, name));
        });
    }
}

function nullOrUndef(input: any) {
    return input === undefined || input === null;
}

function getValue(input: any, attribute: string | number) {
    return nullOrUndef(input) || typeof input !== 'object' ?
        undefined :
        input[attribute];
}

function isPDF(filename) {
    return filename.toLowerCase().endsWith('.pdf');
}

/** PDF view title change  */
export async function pdfTitleChange(fileURL: any, fileName: string, mimeType: string): Promise<Blob> {
    const response = await fetch(fileURL);
    const arrayBuffer = await response.arrayBuffer();

    let pdfBytes = null;
    if (isPDF(fileName)) {
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        pdfDoc.setTitle(fileName);

        pdfBytes = await pdfDoc.save();
    } else {
        pdfBytes = arrayBuffer;
    }

    // Only allow a fixed set of safe MIME types for inline preview. A user-supplied
    // mimeType such as 'text/html' would otherwise open a same-origin blob: URL and
    // execute embedded scripts. Anything else is served as a generic binary so the
    // browser downloads it instead of rendering/executing it.
    const ALLOWED_PREVIEW_MIME = new Set([
        'application/pdf',
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/gif',
        'image/webp',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel.sheet.macroenabled.12', 
        'application/vnd.ms-excel',
        'text/csv'
    ]);
    const safeType = ALLOWED_PREVIEW_MIME.has((mimeType || '').toLowerCase())
        ? mimeType
        : 'application/octet-stream';

    const newBlob = new Blob([pdfBytes], { type: safeType });

    return newBlob;
}


export function isUserInRole(userRoleArr: string[], role: UserRole): boolean {
    return userRoleArr.includes(role)
}

export function sharePointURL(listName: string, viewId: string, mnemonic: string): string {
    return `${APP_CONSTANTS.SHAREPOINT_URL}${listName}/AllItems.aspx?viewid=${viewId}&useFiltersInViewXml=1&FilterField1=Mnemonic&FilterValue1=${mnemonic}&FilterType1=Text&FilterOp1=In`;
}

export function getLastEightQuarters(): string[] {
    const d = new Date();
    let q = Math.floor(d.getMonth() / 3);
    let y = d.getFullYear();
    return Array.from({ length: 8 }, () => {
        if (q-- === 0) { q = 3; y--; }
        const val = `${y}-Q${q + 1}`;
        return val;
    });
}

export function convertIntoMillionsCommon(value, millions) {
    if (millions) {
        if (value >= 0 && value <= 1) {
            return (value);
        }
        else {
            return (value as number / 1000000.0);
        }

    }
    else {
        return value ? (value as number) : 0;
    }

}

export function findParamUpTreeCommon(route: ActivatedRoute, paramName: string): Observable<string | null> {
    // Start with the current route and check for the parameter
    let currentRoute = route;
    while (currentRoute) {
        if (currentRoute.snapshot.paramMap.has(paramName)) {
            // If the parameter is found, return it as an Observable
            return currentRoute.paramMap.pipe(map(paramMap => paramMap.get(paramName)));
        }
        // Move up to the parent route if the parameter is not found at the current level
        currentRoute = currentRoute.parent;
    }
    // If the parameter was not found in any route, return null
    return of(null);
}
